import * as api from '@gitbook/api-client';
import {
    getSiteProductDefinition,
    mapAdvancedCustomizationsToReadableLabels,
} from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import { Alert, TooltipIndicator } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import { getSiteVisibilityLabel } from './SiteVisibility.utils';

/**
 * Displays an alert summarizing the features a site will lose when
 * downgrading from one plan to another. The listed features reflect
 * the site's actual paid usage. Descriptions are dynamically generated
 * based on the site's configuration, current features, and the plan change.
 */
export function SiteFeatureLossAlert(props: { site: api.Site; toPlan: api.SiteType }) {
    const { site, toPlan } = props;
    const { title: toPlanTitle } = getSiteProductDefinition(toPlan);
    const isSamePlan = site.type === toPlan;

    if (isSamePlan) {
        return null;
    }

    // Create a list of features that will be lost by moving to the target plan
    const lostFeatures = site.features
        .filter((feature) => feature.plan > toPlan)
        .map((feature) => ({
            key: feature.id,
            label: getLabelForFeature({
                site,
                feature,
                selectedSiteType: toPlan,
            }),
        }))
        .filter((feature) => feature.label !== null);

    if (lostFeatures.length === 0) {
        return null;
    }

    return (
        <Alert
            kind="danger"
            size="small"
            details={
                <div className="w-full max-w-full gap-1">
                    <p>Switching to {toPlanTitle} will affect these site settings:</p>
                    <ul className="flex flex-col items-start gap-1 pt-2">
                        {lostFeatures.map((feature) => (
                            <FeatureListItem key={feature.key} label={feature.label} />
                        ))}
                    </ul>
                </div>
            }
        />
    );
}

function FeatureListItem({ label }) {
    return (
        <li className="flex items-center gap-2 text-muted">
            <Icon.X size={16} className="m-nudge-icon shrink-0 text-danger" />
            {label}
        </li>
    );
}

function getLabelForFeature(props: {
    site: api.Site;
    feature: api.SiteFeature;
    selectedSiteType: api.SiteType;
}) {
    const { site, feature, selectedSiteType } = props;

    // Sponsored site ads
    if (
        site.type === api.SiteType.Sponsored &&
        selectedSiteType !== api.SiteType.Sponsored &&
        site.ads?.status
    ) {
        return (
            <span>
                <strong>Ads</strong> will be disabled
                {site.ads.status === api.SiteAdsStatus.Live
                    ? ' and no longer be visible on your site'
                    : ''}
            </span>
        );
    }

    // Paid features
    switch (feature.id) {
        case api.SiteFeatureKey.SitesVisitorAuthentication:
            switch (selectedSiteType) {
                case api.SiteType.Premium: {
                    return (
                        <span>
                            <strong>Visibility</strong> will revert to{' '}
                            <strong>{getSiteVisibilityLabel(api.SiteVisibility.Unlisted)}</strong>.
                        </span>
                    );
                }
                case api.SiteType.Basic: {
                    return (
                        <span>
                            <strong>Visibility</strong> will revert to{' '}
                            <strong>{getSiteVisibilityLabel(api.SiteVisibility.Public)}</strong>.
                        </span>
                    );
                }
            }

            return null;

        case api.SiteFeatureKey.SitesSections:
            return (
                <span>
                    <strong>Site sections</strong> will become hidden.
                </span>
            );

        case api.SiteFeatureKey.SitesAdaptiveContent:
            return (
                <span>
                    <strong>Adaptive content</strong> will stop being computed.
                </span>
            );

        case api.SiteFeatureKey.SitesCustomFonts:
            return (
                <span>
                    <strong>Custom fonts</strong> will be replaced by basic fonts.
                </span>
            );

        case api.SiteFeatureKey.SitesRedirects:
            return (
                <span>
                    <strong>Redirects management</strong> will be disabled.
                </span>
            );

        case api.SiteFeatureKey.SitesCustomDomain:
            return (
                <span>
                    <strong>Custom domain</strong> will be replaced with a free GitBook domain.
                </span>
            );

        case api.SiteFeatureKey.SitesAiSearch:
            return (
                <span>
                    <strong>AI search</strong> will be disabled.
                </span>
            );

        case api.SiteFeatureKey.SitesPdfExport:
            return (
                <span>
                    <strong>PDF export</strong> capabilities will be disabled.
                </span>
            );

        case api.SiteFeatureKey.SitesPageFeedback:
            return (
                <span>
                    <strong>Page ratings</strong> will no longer be visible
                </span>
            );

        case api.SiteFeatureKey.SitesUnlistedVisibility:
            return (
                <span>
                    <strong>Visibility</strong> will revert to{' '}
                    <strong>{getSiteVisibilityLabel(api.SiteVisibility.Public)}</strong>.
                </span>
            );

        case api.SiteFeatureKey.SitesShareLinks:
            return (
                <span>
                    <strong>Visibility</strong> will revert to{' '}
                    <strong>{getSiteVisibilityLabel(api.SiteVisibility.Public)}</strong>.
                </span>
            );

        case api.SiteFeatureKey.SitesAdvancedCustomization:
            return (
                <span className="flex gap-2">
                    All <strong>advanced customizations</strong> will be disabled.
                    {feature.customizations && feature.customizations.length > 0 ? (
                        <TooltipIndicator
                            label={mapAdvancedCustomizationsToReadableLabels(
                                feature.customizations
                            ).join(', ')}
                        />
                    ) : null}
                </span>
            );
        case api.SiteFeatureKey.SitesAdvancedInsights:
            return (
                <span>
                    <strong>Advanced insights</strong> will be disabled.
                </span>
            );
        case api.SiteFeatureKey.SitesAiAssistant:
            return (
                <span>
                    <strong>AI assistant</strong> will be disabled.
                </span>
            );
        case api.SiteFeatureKey.SitesCustomSubdirectory:
            return (
                <span>
                    <strong>Custom subdirectory</strong> will be removed.
                </span>
            );
        case api.SiteFeatureKey.SitesSearchInsights:
            return (
                <span>
                    <strong>Search insights</strong> will be disabled.
                </span>
            );
        case api.SiteFeatureKey.SitesAds:
        case api.SiteFeatureKey.SitesApiPlayground:
        case api.SiteFeatureKey.SitesBasicCustomization:
        case api.SiteFeatureKey.SitesFullTextSearch:
        case api.SiteFeatureKey.SitesMultivariantSite:
        case api.SiteFeatureKey.SitesNoCustomDomain:
        case api.SiteFeatureKey.SitesPageTrafficInsights:
        case api.SiteFeatureKey.SitesPreviewDeployments:
        case api.SiteFeatureKey.SitesPublicVisibility:
        case api.SiteFeatureKey.SitesSeo:
        case api.SiteFeatureKey.SitesUserContribution:
        case api.SiteFeatureKey.SitesVisitors:
        case api.SiteFeatureKey.SitesEmbed:
            return null;
        default:
            assertNever(feature.id, `Unknown site feature: ${feature.id}`);
    }
}
